'use client'

import type { UseNotificationToastProps } from '@maestro/commerce-blocks/components/notification/notification.types'

import { useCallback, useEffect, useRef } from 'react'

import { useNotificationToastFocus } from '@maestro/commerce-blocks/hooks/use-notification-toast-focus'
import { useA11y } from '@maestro/core/a11y/a11y.context'
import { createLogger } from '@maestro/core/logger'
import { useClickOutsideElement } from '@pearlconvert/ui/hooks/use-click-outside-element'

const log = createLogger('maestro:commerce-blocks:notification')

export const useNotificationToast = (props: UseNotificationToastProps) => {
  const {
    id,
    content,
    onClose,
    title,
    description = '',
    announceMessage,
    type,
  } = props

  const toastRef = useRef<HTMLDivElement>(null)
  const ignoreMountFocusRef = useRef(false)
  const lastInputWasKeyboardRef = useRef(false)
  const addToCartToast = content !== undefined
  const { announce } = useA11y()
  const shouldAnnounceToast = type !== 'added-to-cart-notification'
  const {
    isToastUserInteracted,
    markToastUserInteracted,
    getNotificationFocusTarget,
    clearNotificationFocusTarget,
  } = useNotificationToastFocus()

  useEffect(() => {
    if (!toastRef.current) {
      return
    }

    ignoreMountFocusRef.current = true

    let mountFocusGuardTimer: ReturnType<typeof setTimeout> | undefined

    const mountFocusTimer = setTimeout(() => {
      toastRef.current?.focus({ preventScroll: true })

      mountFocusGuardTimer = setTimeout(() => {
        ignoreMountFocusRef.current = false
      }, 150)
    }, 100)

    return () => {
      clearTimeout(mountFocusTimer)
      if (mountFocusGuardTimer) {
        clearTimeout(mountFocusGuardTimer)
      }
      lastInputWasKeyboardRef.current = false
      ignoreMountFocusRef.current = false
    }
  }, [])

  const handleClose = useCallback(() => {
    const elementToFocus = getNotificationFocusTarget()
    const shouldRestoreFocus =
      type !== 'added-to-cart-notification' ||
      (id !== undefined && isToastUserInteracted(id))

    onClose?.()

    clearNotificationFocusTarget()

    if (shouldRestoreFocus && elementToFocus) {
      setTimeout(() => {
        try {
          if (
            elementToFocus &&
            elementToFocus instanceof HTMLElement &&
            document.body.contains(elementToFocus) &&
            typeof elementToFocus.focus === 'function'
          ) {
            elementToFocus.focus()
            if (document.activeElement !== elementToFocus) {
              elementToFocus.focus()
            }
          }
        } catch (error) {
          log.error('Focus restoration failed:', error)
        }
      }, 250)
    }
  }, [
    clearNotificationFocusTarget,
    getNotificationFocusTarget,
    id,
    isToastUserInteracted,
    onClose,
    type,
  ])

  useClickOutsideElement(toastRef, handleClose)

  useEffect(() => {
    if (id === undefined || !toastRef.current) {
      return
    }

    const el = toastRef.current

    const handleKeyDown = () => {
      lastInputWasKeyboardRef.current = true
    }

    const handlePointerDown = () => {
      lastInputWasKeyboardRef.current = false
    }

    const handleFocusIn = () => {
      if (ignoreMountFocusRef.current) {
        return
      }

      if (lastInputWasKeyboardRef.current) {
        markToastUserInteracted(id)
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('pointerdown', handlePointerDown, true)
    el.addEventListener('focusin', handleFocusIn)

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('pointerdown', handlePointerDown, true)
      el.removeEventListener('focusin', handleFocusIn)
      lastInputWasKeyboardRef.current = false
    }
  }, [id, markToastUserInteracted])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation() // Prevent ESC from closing parent modals/slide-ins
        handleClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleClose])

  const isNewRef = useRef(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      isNewRef.current = false
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      if (!isNewRef.current) {
        handleClose()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleClose])

  useEffect(() => {
    if (!shouldAnnounceToast) {
      return
    }

    const titleStr = typeof title === 'string' ? title : ''
    const descStr = typeof description === 'string' ? description : ''
    const message = announceMessage || `${titleStr}${descStr}`

    if (!message) {
      return
    }

    announce(message, 'assertive')
  }, [announce, announceMessage, description, shouldAnnounceToast, title])

  return {
    addToCartToast,
    toastRef,
    handleClose,
  }
}
